import { flash, FormGroup, Input, Label, Modal, SubmitButton } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Event, Timeslot } from "types";
import { createEvent, EventCapacityUpdate, getCapacityAdjustment, updateTimeslotCache } from "./EditEventCapacityModal";
import { api } from "@/api/api";
import { useAuthorization } from "@/hooks/useAuthorization";

interface CreateEventNoteModalProps {
    events: Event[];
    timeslots: Timeslot[];
    onClose: (e: any) => void;
    isOpen: boolean;
}

export const CapMultipleEventsModal = ({ events = [], timeslots = [], onClose, isOpen }: CreateEventNoteModalProps) => {
    const hasSelectedEventsOrTimeslots = (events && events.length > 0) || (timeslots && timeslots.length > 0);

    if (!hasSelectedEventsOrTimeslots) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent events={events} timeslots={timeslots} onClose={onClose} />
        </Modal>
    );
};

interface ModalContentProps {
    events: Event[];
    timeslots: Timeslot[];
    onClose: (e?: any) => void;
}

const ModalContent = ({ events, timeslots, onClose }: ModalContentProps) => {
    const { isGranted } = useAuthorization();

    const [reason, setReason] = useState("");

    const { mutateAsync: updateEvent, isLoading: updatingEvent } = useMutation(async (payload: EventCapacityUpdate) => {
        try {
            const { data: event } = await api.put<Event>(`/events/${payload.id}`, payload);
            return event;
        } catch {
            return null;
        }
    });

    const { mutateAsync: createAndCapEvent, isLoading: creatingEvent } = useMutation(async (timeslot: Timeslot) => {
        try {
            return await createEvent(timeslot, timeslot.capacity.reserved, reason);
        } catch {
            return null;
        }
    });

    const handleReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value);

    const handleSubmit = async () => {
        try {
            // Update existing events
            const updatedEvents = await Promise.all(
                events.map(async (event) =>
                    updateEvent({
                        ...event,
                        max: event.quantity.reserved,
                        capacityAdjustment: getCapacityAdjustment(reason),
                    }),
                ),
            );

            // Create events for timeslots that don't have events
            const createdEvents = await Promise.all(timeslots.map(async (timeslot) => createAndCapEvent(timeslot)));

            flash.success(`${events.length + timeslots.length} event(s) capped to seats reserved quantity`);

            const updateParams = {
                isGranted: isGranted,
            };

            // Update cache for existing events
            for (const [index] of events.entries()) {
                const updatedEvent = updatedEvents[index];
                if (updatedEvent) {
                    // Find the corresponding timeslot for this event
                    const timeslotParams = {
                        timeslot: null,
                        event: updatedEvent,
                        productId: null,
                        startDate: null,
                    };

                    await updateTimeslotCache(timeslotParams, updateParams);
                }
            }

            // Update cache for newly created events using their timeslots
            for (const [index, timeslot] of timeslots.entries()) {
                const createdEvent = createdEvents[index];
                if (createdEvent) {
                    const timeslotParams = {
                        timeslot: timeslot,
                        event: createdEvent,
                        productId: null,
                        startDate: null,
                    };

                    await updateTimeslotCache(timeslotParams, updateParams);
                }
            }
        } catch {
            flash.error("Something went wrong while capping events");
        }

        onClose();
    };

    return (
        <div>
            <Modal.Header>Cap Multiple Events</Modal.Header>

            <Modal.Body>
                <span className="block space-y-4">
                    <p>{`Are you sure you want to cap ${events.length + timeslots.length} events?`}</p>
                    <p>
                        These events will no longer accept any new reservations. Active reservations for these event
                        will be retained.
                    </p>
                    <p>Reservationists will still be able to modify capacity manually.</p>
                </span>

                <FormGroup className="mt-6">
                    <Label>Reason</Label>
                    <Input
                        value={reason}
                        placeholder="Reason"
                        data-testid="cap-multiple-events-modal-reason"
                        onChange={handleReasonChange}
                    />
                </FormGroup>
            </Modal.Body>

            <Modal.Footer className="space-x-4">
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <SubmitButton isLoading={updatingEvent || creatingEvent} onClick={handleSubmit}>
                    Confirm
                </SubmitButton>
            </Modal.Footer>
        </div>
    );
};
